<template>
  <v-container>
    <v-card>
      <v-row align="center" class="py-1 px-2" no-gutters>
        <v-col cols="12" sm="6">
          <v-breadcrumbs class="px-2" :items="breadcrumbsItems"></v-breadcrumbs>
        </v-col>
        <v-col
          cols="12"
          sm="6"
          :class="$store.getters.LANGUAGE == 'fa' ? 'text-left' : 'text-right'"
        >
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                fab
                small
                class="mx-2"
                v-bind="attrs"
                v-on="on"
                color="orange"
                @click="helpDialog = true"
              >
                <v-icon small>fa-question</v-icon>
              </v-btn>
            </template>
            <span>{{ $vuetify.lang.translator("showHelp") }}</span>
          </v-tooltip>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12" md="6">
          <line-chart
            :data="cpuData"
            :options="cpuOptions"
            :datasetLabel="cpuDatasetsLabel"
          ></line-chart>
        </v-col>
        <v-col cols="12" md="6">
          <line-chart
            :data="memoryData"
            :options="memoryOptions"
            :datasetLabel="memoryDatasetsLabel"
          ></line-chart>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12" md="6">
          <pie-chart
            :data="diskData"
            :options="diskOptions"
            :labels="diskLabels"
            :colors="diskColors"
          ></pie-chart>
        </v-col>
        <v-col cols="12" md="6">
          <v-data-table
            :headers="headers"
            :items="items"
            :loading="loading"
            :items-per-page="itemsPerPage"
            @update:items-per-page="updateRecordsPerPage"
            :footer-props="{ itemsPerPageOptions: [10, 25, 50, 100] }"
            :options.sync="options"
            :dense="showDense"
            class="elevation-2"
            :caption="$vuetify.lang.translator('services')"
            disable-sort
          >
            <template v-slot:item.index="{ item }">
              {{
                (options.page - 1) * options.itemsPerPage +
                  items.indexOf(item) +
                  1
              }}
            </template>
          </v-data-table>
        </v-col>
      </v-row>
    </v-card>

    <v-dialog
      v-model="helpDialog"
      persistent
      max-width="800"
      @keydown.esc="helpDialog = false"
      transition="dialog-top-transition"
    >
      <v-card>
        <v-toolbar dense elevation="2">
          <v-toolbar-title>{{
            $vuetify.lang.translator("help")
          }}</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-toolbar-items>
            <v-btn icon @click="helpDialog = false">
              <v-icon>fa-times</v-icon>
            </v-btn>
          </v-toolbar-items>
        </v-toolbar>
        <v-card-text>
          <div class="mt-6" v-html="$vuetify.lang.translator('osHelp')"></div>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-btn color="success" @click="helpDialog = false">{{
            $vuetify.lang.translator("gotIt")
          }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { handleError } from "@/helpers/helper";
import LineChart from "@/components/LineChart";
import PieChart from "@/components/PieChart";

export default {
  name: "OS",
  components: {
    LineChart,
    PieChart
  },
  data: () => ({
    breadcrumbsItems: [],
    headers: [],
    items: [],
    itemsPerPage: 0,
    options: {},
    totalItems: 0,
    loading: true,
    showDense: true,
    viewIsOpen: true,
    cpuData: [],
    cpuOptions: {},
    cpuDatasetsLabel: "",
    memoryData: [],
    memoryOptions: {},
    memoryDatasetsLabel: "",
    diskData: [],
    diskOptions: {},
    diskLabels: [],
    diskColors: ["red", "green"],
    helpDialog: false
  }),
  created() {
    this.itemsPerPage = parseInt(this.$store.getters.PERPAGE);
    this.showDense = this.$store.getters.SHOWDENSE === "show";

    this.breadcrumbsItems.push({
      text: this.$vuetify.lang.translator("dashboard"),
      to: { name: "Dashboard" }
    });
    this.breadcrumbsItems.push({
      text: this.$vuetify.lang.translator("server")
    });
    this.breadcrumbsItems.push({
      text: this.$vuetify.lang.translator("systemStatus"),
      to: { name: "OS" }
    });

    this.headers.push({
      text: this.$vuetify.lang.translator("row"),
      value: "index",
      sortable: false,
      align: "center"
    });
    this.headers.push({
      text: this.$vuetify.lang.translator("name"),
      value: "name"
    });
    this.headers.push({
      text: this.$vuetify.lang.translator("status"),
      value: "status"
    });

    this.cpuDatasetsLabel = this.$vuetify.lang.translator("usagePercentage");
    this.cpuOptions = {
      responsive: true,
      maintainAspectRatio: false,
      legend: {
        labels: {
          fontFamily: "'Vazir FD', Tahoma"
        }
      },
      title: {
        display: true,
        text: this.$vuetify.lang.translator("cpuUsage"),
        fontFamily: "'Vazir FD', Tahoma"
      },
      scales: {
        xAxes: [
          {
            display: true,
            scaleLabel: {
              display: true,
              labelString: this.$vuetify.lang.translator("oneMinuteInterval"),
              fontFamily: "'Vazir FD', Tahoma"
            }
          }
        ],
        yAxes: [
          {
            display: true,
            scaleLabel: {
              display: true,
              labelString: this.$vuetify.lang.translator("percentage"),
              fontFamily: "'Vazir FD', Tahoma"
            }
          }
        ]
      }
    };

    this.memoryDatasetsLabel = this.$vuetify.lang.translator("usagePercentage");
    this.memoryOptions = {
      responsive: true,
      maintainAspectRatio: false,
      legend: {
        labels: {
          fontFamily: "'Vazir FD', Tahoma"
        }
      },
      title: {
        display: true,
        text: this.$vuetify.lang.translator("memoryUsage"),
        fontFamily: "'Vazir FD', Tahoma"
      },
      scales: {
        xAxes: [
          {
            display: true,
            scaleLabel: {
              display: true,
              labelString: this.$vuetify.lang.translator("oneMinuteInterval"),
              fontFamily: "'Vazir FD', Tahoma"
            }
          }
        ],
        yAxes: [
          {
            display: true,
            scaleLabel: {
              display: true,
              labelString: this.$vuetify.lang.translator("percentage"),
              fontFamily: "'Vazir FD', Tahoma"
            }
          }
        ]
      }
    };

    this.diskOptions = {
      responsive: true,
      maintainAspectRatio: false,
      legend: {
        labels: {
          fontFamily: "'Vazir FD', Tahoma"
        }
      },
      title: {
        display: true,
        text: this.$vuetify.lang.translator("hardSpace"),
        fontFamily: "'Vazir FD', Tahoma"
      }
    };

    this.diskLabels.push(this.$vuetify.lang.translator("used"));
    this.diskLabels.push(this.$vuetify.lang.translator("available"));

    this.checkPrivileges();
    this.getCPUData();
    this.getMemoryData();
    this.getDiskData();
    this.getServices();
  },
  destroyed() {
    this.viewIsOpen = false;
  },
  methods: {
    checkPrivileges() {
      let userType = parseInt(this.$store.getters.USERTYPE);
      let allowed_privileges = [1];
      if (allowed_privileges.indexOf(userType) === -1) {
        this.$router.push({ name: "Error403" });
      }
    },
    getCPUData() {
      var self = this;
      this.$http
        .get("/os/cpu/")
        .then(function(response) {
          if (self.cpuData.length == 10) {
            self.cpuData.shift();
          }
          self.cpuData.push(response.data);
          if (self.viewIsOpen) {
            setTimeout(self.getCPUData, 10000);
          }
        })
        .catch(function(error) {
          let result = handleError(
            error,
            self.$toasted,
            self.$router,
            self.$store
          );
          for (var item in result) {
            self.$toasted.error(result[item], {
              duration: 5000,
              keepOnHover: true,
              className:
                self.$store.getters.LANGUAGE == "fa"
                  ? "vazir-font-fd"
                  : "vazir-font",
              iconPack: "fontawesome",
              icon: "exclamation-triangle",
              position: "top-center"
            });
          }
        });
    },
    getMemoryData() {
      var self = this;
      this.$http
        .get("/os/memory/")
        .then(function(response) {
          if (self.memoryData.length == 10) {
            self.memoryData.shift();
          }
          self.memoryData.push(response.data[2]);
          if (self.viewIsOpen) {
            setTimeout(self.getMemoryData, 10000);
          }
        })
        .catch(function(error) {
          let result = handleError(
            error,
            self.$toasted,
            self.$router,
            self.$store
          );
          for (var item in result) {
            self.$toasted.error(result[item], {
              duration: 5000,
              keepOnHover: true,
              className:
                self.$store.getters.LANGUAGE == "fa"
                  ? "vazir-font-fd"
                  : "vazir-font",
              iconPack: "fontawesome",
              icon: "exclamation-triangle",
              position: "top-center"
            });
          }
        });
    },
    getDiskData() {
      this.diskData = [];
      var self = this;
      this.$http
        .get("/os/disk/")
        .then(function(response) {
          let used = parseInt(response.data[3]);
          self.diskData.push(used);
          let free = 100 - used;
          self.diskData.push(free);
          if (self.viewIsOpen) {
            setTimeout(self.getDiskData, 30000);
          }
        })
        .catch(function(error) {
          let result = handleError(
            error,
            self.$toasted,
            self.$router,
            self.$store
          );
          for (var item in result) {
            self.$toasted.error(result[item], {
              duration: 5000,
              keepOnHover: true,
              className:
                self.$store.getters.LANGUAGE == "fa"
                  ? "vazir-font-fd"
                  : "vazir-font",
              iconPack: "fontawesome",
              icon: "exclamation-triangle",
              position: "top-center"
            });
          }
        });
    },
    getServices() {
      this.loading = true;
      var self = this;
      this.$http
        .get("/os/service/")
        .then(function(response) {
          self.items = response.data;
        })
        .catch(function(error) {
          let result = handleError(
            error,
            self.$toasted,
            self.$router,
            self.$store
          );
          for (var item in result) {
            self.$toasted.error(result[item], {
              duration: 5000,
              keepOnHover: true,
              className:
                self.$store.getters.LANGUAGE == "fa"
                  ? "vazir-font-fd"
                  : "vazir-font",
              iconPack: "fontawesome",
              icon: "exclamation-triangle",
              position: "top-center"
            });
          }
        })
        .then(function() {
          self.loading = false;
        });
    },
    updateRecordsPerPage(rowsPerPage) {
      this.$store.dispatch("saveRecordsPerPage", rowsPerPage);
    }
  }
};
</script>
